import { getDoc, doc } from "firebase/firestore";
import { db } from "./firebase.js";
import { logger } from "../utils/logger";
import { setUserFavoriteTeam } from "./firestoreService.js";

/**
 * @file This service provides a function for fetching the user's profile data from Firestore.
 */

const SPORTS_DB_TO_API_FOOTBALL_TEAM_MAP = {
  "133604": "42", // Arsenal
  "133601": "66", // Aston Villa
  "134301": "35", // Bournemouth
  "133600": "55", // Brentford
  "133619": "51", // Brighton
  "133610": "49", // Chelsea
  "133605": "52", // Crystal Palace
  "133607": "45", // Everton
  "133608": "36", // Fulham
  "133618": "57", // Ipswich Town
  "133626": "46", // Leicester City
  "133602": "40", // Liverpool
  "133613": "50", // Manchester City
  "133612": "33", // Manchester United
  "133614": "34", // Newcastle United
  "133615": "65", // Nottingham Forest
  "133617": "41", // Southampton
  "133616": "47", // Tottenham Hotspur
  "133624": "48", // West Ham
  "133623": "39", // Wolverhampton Wanderers
  "133603": "44", // Burnley
  "133611": "63", // Leeds
  "133620": "1359", // Luton
  "133621": "62", // Sheffield United
};

// Helper to measure performance of async operations
const measureUserServicePerformance = async (operationName, promiseFn) => {
  const start = performance.now();
  try {
    const result = await promiseFn();
    const end = performance.now();
    logger.log(`userService: ${operationName} completed in ${(end - start).toFixed(2)} ms`);
    return result;
  } catch (error) {
    const end = performance.now();
    logger.error(`userService: ${operationName} failed in ${(end - start).toFixed(2)} ms`, error);
    throw error;
  }
};

/**
 * Gets the user's profile data from Firestore.
 *
 * @param {string} uid - The user's ID.
 * @returns {Promise<object|null>} A promise that resolves with the user's profile data, or null if the user is not found.
 */
export const getUserProfileData = async (uid) => {
  if (!uid) {
    logger.warn("userService: getUserProfileData called without a UID.");
    return null;
  }

  return measureUserServicePerformance("getUserProfileData", async () => {
    try {
      logger.log(`userService: Fetching profile data for UID: ${uid}`);
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Auto-heal legacy TheSportsDB favoriteTeamId to API-Football ID
        if (userData?.favoriteTeamId) {
          const mappedTeamId = SPORTS_DB_TO_API_FOOTBALL_TEAM_MAP[String(userData.favoriteTeamId).trim()];
          if (mappedTeamId) {
            logger.log(`userService: Auto-migrating legacy favoriteTeamId ${userData.favoriteTeamId} -> ${mappedTeamId}`);
            userData.favoriteTeamId = mappedTeamId;
            setUserFavoriteTeam(uid, mappedTeamId).catch((err) => {
              logger.warn("userService: Failed to persist migrated favoriteTeamId to Firestore:", err);
            });
          }
        }

        logger.log(`userService: Successfully fetched profile data for UID: ${uid}`, userData);
        return userData; // Return the whole document data
      } else {
        logger.warn(`userService: No user document found for UID: ${uid}`);
        return null;
      }
    } catch (error) {
      logger.error(`userService: Error fetching user profile data for UID: ${uid}`, error);
      throw error;
    }
  });
};
